import jsPDF from 'jspdf';
import { drawPdfText } from './pdfUtils';
import {
  ReportCompanyHeader,
  isReportHeaderValuePresent,
} from './reportHeader';
import { formatTinForInput } from 'utils/tinUtils';
import { DEFAULT_CURRENCY_OPTIONS } from 'utils/currencies';

export const REPORT_PDF_PAGE_WIDTH = 210;
export const REPORT_PDF_PAGE_HEIGHT = 297;
export const REPORT_PDF_MARGIN = 15;
export const REPORT_PDF_FONT_SIZE = 11;
/** Bottom inset (no page-number footer) */
export const REPORT_PDF_MAX_Y = REPORT_PDF_PAGE_HEIGHT - REPORT_PDF_MARGIN - 5;

const HEADER_LINE_STEP_MM = 4.5;
const HEADER_BOTTOM_PAD_MM = 10;

/** Usable width for centered header lines (matches left/right page margins). */
export const REPORT_PDF_HEADER_TEXT_MAX_WIDTH_MM =
  REPORT_PDF_PAGE_WIDTH - 2 * REPORT_PDF_MARGIN;

/** Vertical step between stacked centered header lines (address lines, TIN, etc.). */
export const REPORT_PDF_HEADER_LINE_STEP_MM = HEADER_LINE_STEP_MM;

/**
 * Draws the company address centered, wrapping within page margins.
 * @returns Baseline Y for the next header line (e.g. TIN), i.e. after the last address line.
 */
export function drawWrappedCenteredAddress(
  pdf: jsPDF,
  xCenter: number,
  firstLineBaselineY: number,
  rawAddress: string,
  options: {
    fontSize: number;
    fontFamily: string;
    isEditable: boolean;
    lineStepMm?: number;
  },
): number {
  const lineStep = options.lineStepMm ?? REPORT_PDF_HEADER_LINE_STEP_MM;
  const addressText = rawAddress.trim();
  pdf.setFont(options.fontFamily, 'normal');
  pdf.setFontSize(options.fontSize);
  const lines = pdf.splitTextToSize(
    addressText,
    REPORT_PDF_HEADER_TEXT_MAX_WIDTH_MM,
  ) as string[];
  const n = Math.max(1, lines.length);

  if (options.isEditable) {
    drawPdfText(pdf, addressText, xCenter, firstLineBaselineY, {
      fontSize: options.fontSize,
      textAlign: 'center',
      fontName: options.fontFamily,
      maxWidth: REPORT_PDF_HEADER_TEXT_MAX_WIDTH_MM,
      multiline: true,
      editable: true,
    });
  } else {
    let lineY = firstLineBaselineY;
    for (const line of lines) {
      const lineWidth = pdf.getTextWidth(line);
      drawPdfText(pdf, line, xCenter, lineY, {
        fontSize: options.fontSize,
        textAlign: 'center',
        fontName: options.fontFamily,
        width: lineWidth,
        editable: false,
      });
      lineY += lineStep;
    }
  }

  return firstLineBaselineY + n * lineStep;
}

/** Minimal context for {@link drawFinancialReportHeader} */
export type FinancialReportPdfHeaderContext = {
  pdf: jsPDF;
  y: number;
  reportTitle: string;
  fontFamily: string;
  isEditable: boolean;
};

function getCurrencyDisplayName(currency: string): string {
  const normalized = (currency || 'PHP').trim();
  const upper = normalized.toUpperCase();
  const option = DEFAULT_CURRENCY_OPTIONS.find((item) => item.code === upper);
  if (option) {
    return option.label.replace(/^[^A-Za-z]+/, '').trim();
  }
  if (normalized.length > 3) {
    return normalized;
  }
  return upper;
}

/**
 * Shared header for financial report PDFs: Noto-ready, conditional address/TIN,
 * formatted TIN, company name normal weight, report title bold, period line.
 */
export function drawFinancialReportHeader(
  ctx: FinancialReportPdfHeaderContext,
  period: string,
  companyHeader: ReportCompanyHeader,
  options?: {
    amountCurrency?: string;
  },
): void {
  const pdf = ctx.pdf;
  const xCenter = REPORT_PDF_PAGE_WIDTH / 2;

  const showAddress = isReportHeaderValuePresent(
    companyHeader.registeredBusinessAddress,
  );
  const showTin = isReportHeaderValuePresent(
    companyHeader.registeredBusinessTin,
  );

  let y = ctx.y;

  pdf.setFont(ctx.fontFamily, 'normal');
  pdf.setFontSize(REPORT_PDF_FONT_SIZE);
  const nameWidth = pdf.getTextWidth(companyHeader.registeredBusinessName);
  drawPdfText(pdf, companyHeader.registeredBusinessName, xCenter, y, {
    fontSize: REPORT_PDF_FONT_SIZE,
    fontStyle: 'normal',
    textAlign: 'center',
    fontName: ctx.fontFamily,
    width: nameWidth,
    editable: ctx.isEditable,
  });

  if (showAddress || showTin) {
    y += HEADER_LINE_STEP_MM;
  }

  if (showAddress) {
    y = drawWrappedCenteredAddress(
      pdf,
      xCenter,
      y,
      companyHeader.registeredBusinessAddress,
      {
        fontSize: REPORT_PDF_FONT_SIZE,
        fontFamily: ctx.fontFamily,
        isEditable: ctx.isEditable,
      },
    );
  }

  if (showTin) {
    const tinDisplay = formatTinForInput(
      companyHeader.registeredBusinessTin.trim(),
    );
    const tinText = `TIN: ${tinDisplay}`;
    pdf.setFont(ctx.fontFamily, 'normal');
    pdf.setFontSize(REPORT_PDF_FONT_SIZE);
    const tinWidth = pdf.getTextWidth(tinText);
    drawPdfText(pdf, tinText, xCenter, y, {
      fontSize: REPORT_PDF_FONT_SIZE,
      textAlign: 'center',
      fontName: ctx.fontFamily,
      width: tinWidth,
      editable: ctx.isEditable,
    });
    y += HEADER_LINE_STEP_MM;
  }

  if (!showAddress && !showTin) {
    y += HEADER_LINE_STEP_MM;
  }

  pdf.setFont(ctx.fontFamily, 'bold');
  pdf.setFontSize(REPORT_PDF_FONT_SIZE);
  const titleWidth = pdf.getTextWidth(ctx.reportTitle);
  drawPdfText(pdf, ctx.reportTitle, xCenter, y, {
    fontSize: REPORT_PDF_FONT_SIZE,
    fontStyle: 'bold',
    textAlign: 'center',
    fontName: ctx.fontFamily,
    width: titleWidth,
    editable: ctx.isEditable,
  });
  y += HEADER_LINE_STEP_MM;

  pdf.setFont(ctx.fontFamily, 'normal');
  pdf.setFontSize(REPORT_PDF_FONT_SIZE);
  const periodWidth = pdf.getTextWidth(period);
  drawPdfText(pdf, period, xCenter, y, {
    fontSize: REPORT_PDF_FONT_SIZE,
    textAlign: 'center',
    fontName: ctx.fontFamily,
    width: periodWidth,
    editable: ctx.isEditable,
  });
  y += HEADER_LINE_STEP_MM;

  if (options?.amountCurrency) {
    const amountsLine = `(Amounts in ${getCurrencyDisplayName(options.amountCurrency)})`;
    const amountsWidth = pdf.getTextWidth(amountsLine);
    drawPdfText(pdf, amountsLine, xCenter, y, {
      fontSize: REPORT_PDF_FONT_SIZE,
      textAlign: 'center',
      fontName: ctx.fontFamily,
      width: amountsWidth,
      editable: ctx.isEditable,
    });
    y += HEADER_LINE_STEP_MM;
  }

  y += HEADER_BOTTOM_PAD_MM;

  ctx.y = y;
}
